import { useTranslation } from "react-i18next";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import {
  useMarkPromotie,
  useUndoPromotie,
  type PlanningBaan,
  type PlanningDag,
  type Promotieplaats,
} from "./usePlanning";

/** Per baan, who finished on each promotieplaats (ADR-0080). Stores no uitslag. */
export function PromotieMarkering({
  seizoenId,
  dag,
}: {
  seizoenId: string;
  dag: PlanningDag;
}) {
  const banen = dag.banen.filter((baan) => baan.promotieplaatsen.length > 0);

  // Before any hook: the grid also renders where there is no query client.
  return banen.length === 0 ? null : (
    <PromotieCard seizoenId={seizoenId} banen={banen} />
  );
}

function PromotieCard({
  seizoenId,
  banen,
}: {
  seizoenId: string;
  banen: PlanningBaan[];
}) {
  const { t, i18n } = useTranslation("planning");
  const mark = useMarkPromotie(seizoenId);
  const undo = useUndoPromotie(seizoenId);

  const error = mark.error ?? undo.error;
  const busy = mark.isPending || undo.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("promotie.title")}</CardTitle>
        <CardDescription>{t("promotie.explanation")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {banen.map((baan) => (
          <section key={baan.id} aria-label={baan.label} className="space-y-2">
            <h3 className="text-sm font-medium">{baan.label}</h3>
            <ul className="space-y-2">
              {baan.promotieplaatsen.map((plaats) => (
                <li
                  key={plaats.id}
                  className="flex flex-wrap items-center gap-3 text-sm"
                >
                  <span className="w-56 shrink-0">
                    {t("promotie.plaats", {
                      positie: plaats.positie,
                      bestemming: plaats.bestemming,
                    })}
                  </span>
                  {plaats.ploegId ? (
                    <MarkedPloeg
                      plaats={plaats}
                      busy={busy}
                      onUndo={() => undo.mutate(plaats.id)}
                    />
                  ) : (
                    <div className="min-w-0 flex-1">
                      <Select
                        aria-label={t("promotie.pick", {
                          positie: plaats.positie,
                        })}
                        value=""
                        disabled={busy}
                        onChange={(e) =>
                          e.target.value &&
                          mark.mutate({
                            promotieplaatsId: plaats.id,
                            ploegId: e.target.value,
                          })
                        }
                      >
                        <option value="">{t("promotie.nonePicked")}</option>
                        {candidates(baan, plaats, i18n.language).map(
                          (ploeg) => (
                            <option key={ploeg.id} value={ploeg.id}>
                              {ploeg.naam}
                            </option>
                          ),
                        )}
                      </Select>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}

        {error && <Alert tone="warning">{error.message}</Alert>}
      </CardContent>
    </Card>
  );
}

function MarkedPloeg({
  plaats,
  busy,
  onUndo,
}: {
  plaats: Promotieplaats;
  busy: boolean;
  onUndo: () => void;
}) {
  const { t } = useTranslation("planning");

  return (
    <>
      <span className="font-medium">{plaats.ploegNaam}</span>
      <Button
        variant="ghost"
        size="sm"
        disabled={busy}
        onClick={onUndo}
        aria-label={t("promotie.undoFor", { ploeg: plaats.ploegNaam ?? "" })}
      >
        {t("promotie.undo")}
      </Button>
    </>
  );
}

// Minus what the server would refuse: ploegen marked on this baan, or already entered in the target periode.
function candidates(
  baan: PlanningBaan,
  plaats: Promotieplaats,
  locale: string,
) {
  const marked = new Set([
    ...baan.promotieplaatsen.flatMap((p) => (p.ploegId ? [p.ploegId] : [])),
    ...plaats.ingeschrevenPloegIds,
  ]);

  const ploegen = new Map(
    baan.regels.flatMap((regel) =>
      regel.ploeg && !marked.has(regel.ploeg.id)
        ? [[regel.ploeg.id, regel.ploeg] as const]
        : [],
    ),
  );

  return [...ploegen.values()].sort((a, b) =>
    a.naam.localeCompare(b.naam, locale),
  );
}
